import type { CodexUsageCredits, RateLimitWindow, SessionUsageSummary, StatusPayload } from "../types";

interface StatusBarProps {
  status: StatusPayload | null;
  messageCount: number;
  usage: SessionUsageSummary | null;
  onOpenUsage(): void;
}

export function StatusBar({ status, messageCount, usage, onOpenUsage }: StatusBarProps) {
  const contextValue = usage?.context ? formatPercent(usage.context.usedPercent) : "--";
  const credits = status?.quotas?.credits ?? null;
  const weekly = status?.quotas?.weekly ?? status?.quotas?.secondary ?? null;

  return (
    <section className="status-bar" aria-label="Statistiques de la conversation et quotas Codex">
      <span className="status-metric" title={`${messageCount} message${messageCount === 1 ? "" : "s"} dans cette conversation`}>
        <small>Messages</small>
        <strong>{messageCount}</strong>
      </span>
      <button className={`status-metric ${usage?.context ? "" : "unavailable"}`} type="button" onClick={onOpenUsage} title={contextTitle(usage)} aria-label={contextTitle(usage)}>
        <small>Contexte</small>
        <strong>{contextValue}</strong>
      </button>
      <button className={`status-metric ${credits ? "" : "unavailable"}`} type="button" onClick={onOpenUsage} title={creditsTitle(credits)} aria-label={creditsTitle(credits)}>
        <small>Crédits</small>
        <strong>{formatCreditsCompact(credits)}</strong>
      </button>
      <button className={`status-metric ${weekly ? "" : "unavailable"}`} type="button" onClick={onOpenUsage} title={quotaTitlePart("7 jours", weekly)} aria-label={quotaTitlePart("7 jours", weekly)}>
        <small>Quota 7 j</small>
        <strong>{weekly ? formatPercent(weekly.remainingPercent) : "--"}</strong>
      </button>
    </section>
  );
}

function contextTitle(usage: SessionUsageSummary | null): string {
  if (!usage?.context) return "Contexte non mesuré. Ouvrir le suivi d’usage.";
  return `Contexte utilisé : ${formatPercent(usage.context.usedPercent)}, ${usage.context.inputTokens} tokens sur ${usage.context.contextWindow}. Ouvrir le détail.`;
}

function quotaTitlePart(label: string, window: RateLimitWindow | null): string {
  if (!window) return `Quota ${label} indisponible.`;
  return `Quota ${label} : ${formatPercent(window.remainingPercent)} restant, remise à zéro ${formatResetLong(window.resetsAt)}.`;
}

function creditsTitle(credits: CodexUsageCredits | null): string {
  if (!credits) return "Solde de crédits indisponible.";
  if (credits.unlimited) return "Crédits illimités.";
  if (credits.balance == null) return credits.hasCredits ? "Crédits disponibles, solde non communiqué." : "Aucun crédit disponible.";
  return `${formatCredits(credits.balance)} crédits restants.`;
}

function formatCreditsCompact(credits: CodexUsageCredits | null): string {
  if (!credits) return "--";
  if (credits.unlimited) return "∞";
  if (credits.balance == null) return credits.hasCredits ? "Oui" : "0";
  const value = Number(credits.balance);
  if (!Number.isFinite(value)) return "--";
  if (value >= 1000) return `${new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 1 }).format(value / 1000)} k`;
  return new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 1 }).format(value);
}

function formatCredits(value: string): string {
  const numeric = Number(value);
  return Number.isFinite(numeric)
    ? new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 2 }).format(numeric)
    : value;
}

function formatPercent(value: number): string {
  return `${new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 1 }).format(value)}%`;
}

function formatResetLong(value: string | null): string {
  if (!value) return "inconnue";
  return new Intl.DateTimeFormat("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}
